import React, { useEffect, useState } from 'react';
import interventionService from '../../../services/interventionService';
import { useTheme } from '../../../contexts/ThemeContext';

const InterventionRapportModal = ({ interventionId, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rapport, setRapport] = useState([]);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (open && interventionId) {
      setLoading(true);
      setError('');
      interventionService.getRapport(interventionId)
        .then(res => {
          if (res.success) {
            setRapport(res.data);
          } else {
            setError(res.message);
          }
        })
        .catch(() => setError('Erreur lors du chargement du rapport'))
        .finally(() => setLoading(false));
    } else {
      setRapport([]);
      setError('');
    }
  }, [open, interventionId]);

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-all ${open ? '' : 'pointer-events-none opacity-0'}`}
      style={{
        background: isDarkMode ? 'rgba(17,24,39,0.3)' : 'rgba(255,255,255,0.12)',
        backdropFilter: 'blur(0.1rem)',
        WebkitBackdropFilter: 'blur(0.1rem)'
      }}
    >
      <div className={`w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col rounded-2xl border ${isDarkMode ? 'bg-[#232a36] border-[#232a36]' : 'bg-white border-gray-200'}`}
        style={{ maxHeight: '90vh', overflowY: 'auto' }}
      >
        {/* Header */}
        <div className={`${isDarkMode ? 'bg-[#232a36]' : 'bg-gradient-to-r from-primary-600 to-primary-700'} px-6 py-4 flex-shrink-0 border-b ${isDarkMode ? 'border-[#232a36]' : 'border-primary-700/40'}`}> 
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`${isDarkMode ? 'bg-primary-500 bg-opacity-10' : 'bg-white bg-opacity-20'} w-10 h-10 rounded-xl flex items-center justify-center mr-3`}>
                <i className={`fas fa-file-alt ${isDarkMode ? 'text-primary-300' : 'text-white'} text-lg`}></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Rapport d'intervention</h2>
                <p className={`${isDarkMode ? 'text-primary-200' : 'text-primary-100'} text-sm`}>Détail du rapport</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>
        {/* Removed duplicate close button */}
        {/* Content */}
        <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          <div className={`${isDarkMode ? 'bg-[#232a36]' : ''} p-6`}> 
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className={`animate-spin rounded-full h-10 w-10 border-b-2 ${isDarkMode ? 'border-primary-400' : 'border-primary-600'}`}></div>
              </div>
            ) : error ? (
              <div className={`p-4 rounded-xl mb-4 ${isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-50 text-red-700'}`}> 
                {error}
              </div>
            ) : rapport.length === 0 ? (
              <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Aucun rapport trouvé pour cette intervention.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className={`min-w-full divide-y rounded-xl overflow-hidden ${isDarkMode ? 'divide-[#2e3746]' : 'divide-gray-200'}`}> 
                  <thead className={isDarkMode ? 'bg-[#232a36]' : 'bg-gray-100'}>
                    <tr>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-primary-200' : 'text-gray-700'}`}>Famille</th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-primary-200' : 'text-gray-700'}`}>Pièce</th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-primary-200' : 'text-gray-700'}`}>Type</th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-primary-200' : 'text-gray-700'}`}>Capacité</th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-primary-200' : 'text-gray-700'}`}>Quantité</th>
                    </tr>
                  </thead>
                  <tbody className={isDarkMode ? 'bg-[#232a36] divide-y divide-[#2e3746]' : 'bg-white divide-y divide-gray-100'}>
                    {rapport.map((item, idx) => (
                      <tr key={idx} className={isDarkMode ? 'hover:bg-primary-800/40 transition-colors' : 'hover:bg-primary-50 transition-colors'}>
                        <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item.famille}</td>
                        <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item.piece}</td>
                        <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item.type}</td>
                        <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item.capacite}</td>
                        <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item.qte}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterventionRapportModal;
